import type { KanbanColuna, KanbanCartao, KanbanRaia, KanbanQuadro, NivelPrioridadeKanban } from '@pieltec/contracts';
import { KanbanRaiaComponent, OcupacaoWipLista } from './kanban-raia';
import { ChevronsLeft, ChevronsRight } from 'lucide-react';
import { larguraColuna, rotuloJanelaConcluido } from '../../../lib/kanban/volume';
import { Button } from '../../../components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../../components/ui/collapsible';
import type { DestinoMover } from '../../../lib/kanban/movimento';

function RaiaNaColuna({
  raia,
  raiasRecolhidas,
  onAlternarRaia,
  onSelecionarCartao,
  podeArrastar,
  aceitaSoltura,
  compativel,
  emFoco,
  quadro,
  onMover,
  onPrioridade,
  onResolverConflito,
  onDesbloquear,
}: {
  raia: KanbanRaia;
  raiasRecolhidas: Set<string>;
  onAlternarRaia?: (raiaId: string) => void;
  onSelecionarCartao?: (cartao: KanbanCartao) => void;
  podeArrastar?: boolean;
  aceitaSoltura?: boolean;
  compativel?: boolean;
  emFoco?: boolean;
  quadro?: KanbanQuadro;
  onMover?: (cartao: KanbanCartao, destino: DestinoMover) => void;
  onPrioridade?: (cartao: KanbanCartao, nivel: NivelPrioridadeKanban) => void;
  onResolverConflito?: (cartao: KanbanCartao) => void;
  onDesbloquear?: (cartao: KanbanCartao) => void;
}) {
  return (
    <KanbanRaiaComponent
      raia={raia}
      recolhida={raiasRecolhidas.has(raia.id)}
      onAlternarRecolher={() => onAlternarRaia?.(raia.id)}
      onSelecionarCartao={onSelecionarCartao}
      podeArrastar={podeArrastar}
      aceitaSoltura={aceitaSoltura}
      compativel={compativel}
      emFoco={emFoco}
      quadro={quadro}
      onMover={onMover}
      onPrioridade={onPrioridade}
      onResolverConflito={onResolverConflito}
      onDesbloquear={onDesbloquear}
    />
  );
}

function renderRaias(
  coluna: KanbanColuna,
  raiasRecolhidas: Set<string>,
  onAlternarRaia?: (raiaId: string) => void,
  onSelecionarCartao?: (cartao: KanbanCartao) => void,
  podeArrastar?: boolean,
  avaliarRaia?: (raia: KanbanRaia) => { aceitaSoltura: boolean; compativel: boolean; emFoco: boolean },
  quadro?: KanbanQuadro,
  onMover?: (cartao: KanbanCartao, destino: DestinoMover) => void,
  onPrioridade?: (cartao: KanbanCartao, nivel: NivelPrioridadeKanban) => void,
  onResolverConflito?: (cartao: KanbanCartao) => void,
  onDesbloquear?: (cartao: KanbanCartao) => void,
) {
  const grupos = coluna.gruposEstufa ?? [];
  const idsNoGrupo = new Set(grupos.flatMap((grupo) => grupo.raiaIds));
  const porId = new Map(coluna.raias.map((raia) => [raia.id, raia]));
  const soltas = coluna.raias.filter((raia) => !idsNoGrupo.has(raia.id));
  return (
    <>
      {grupos.map((grupo) => {
        const raias = grupo.raiaIds
          .map((id) => porId.get(id))
          .filter((raia): raia is KanbanRaia => raia !== undefined);
        const muitas = raias.length > 4;
        const lista = (
          <div className={muitas ? 'flex max-h-80 flex-col gap-2 overflow-y-auto' : 'flex flex-col gap-2'}>
            {raias.map((raia) => {
              const avaliacao = avaliarRaia?.(raia);
              return (
              <RaiaNaColuna
                key={raia.id}
                raia={raia}
                raiasRecolhidas={raiasRecolhidas}
                onAlternarRaia={onAlternarRaia}
                onSelecionarCartao={onSelecionarCartao}
                podeArrastar={podeArrastar}
                aceitaSoltura={avaliacao?.aceitaSoltura}
                compativel={avaliacao?.compativel}
                emFoco={avaliacao?.emFoco}
                quadro={quadro}
                onMover={onMover}
                onPrioridade={onPrioridade}
                onResolverConflito={onResolverConflito}
                onDesbloquear={onDesbloquear}
              />
              );
            })}
          </div>
        );
        return (
          <div key={grupo.estufaId} className="flex flex-col gap-2 rounded-md border border-slate-200 bg-white p-2">
            <div className="text-[11px] font-semibold uppercase text-slate-700">
              Estufa {grupo.codigo} — ocupação
            </div>
            <OcupacaoWipLista ocupacao={grupo.ocupacao} />
            {muitas ? (
              <Collapsible defaultOpen>
                <CollapsibleTrigger className="text-left text-[11px] text-slate-500">
                  Cabines ({raias.length})
                </CollapsibleTrigger>
                <CollapsibleContent>{lista}</CollapsibleContent>
              </Collapsible>
            ) : lista}
          </div>
        );
      })}
      {soltas.map((raia) => {
        const avaliacao = avaliarRaia?.(raia);
        return (
        <RaiaNaColuna
          key={raia.id}
          raia={raia}
          raiasRecolhidas={raiasRecolhidas}
          onAlternarRaia={onAlternarRaia}
          onSelecionarCartao={onSelecionarCartao}
          podeArrastar={podeArrastar}
          aceitaSoltura={avaliacao?.aceitaSoltura}
          compativel={avaliacao?.compativel}
          emFoco={avaliacao?.emFoco}
          quadro={quadro}
          onMover={onMover}
          onPrioridade={onPrioridade}
          onResolverConflito={onResolverConflito}
          onDesbloquear={onDesbloquear}
        />
        );
      })}
    </>
  );
}

interface KanbanColunaProps {
  coluna: KanbanColuna;
  recolhida?: boolean;
  onAlternarRecolher?: () => void;
  raiasRecolhidas?: Set<string>;
  onAlternarRaia?: (raiaId: string) => void;
  onSelecionarCartao?: (cartao: KanbanCartao) => void;
  podeArrastar?: boolean;
  avaliarRaia?: (raia: KanbanRaia) => { aceitaSoltura: boolean; compativel: boolean; emFoco: boolean };
  quadro?: KanbanQuadro;
  onMover?: (cartao: KanbanCartao, destino: DestinoMover) => void;
  onPrioridade?: (cartao: KanbanCartao, nivel: NivelPrioridadeKanban) => void;
  onResolverConflito?: (cartao: KanbanCartao) => void;
  onDesbloquear?: (cartao: KanbanCartao) => void;
  concluidoDias?: number | 'todos';
  onMudarConcluidoDias?: (dias: number | 'todos') => void;
}

export function KanbanColunaComponent({ 
  coluna, 
  recolhida = false,
  onAlternarRecolher,
  raiasRecolhidas = new Set(),
  onAlternarRaia,
  onSelecionarCartao,
  podeArrastar = false,
  avaliarRaia,
  quadro,
  onMover,
  onPrioridade,
  onResolverConflito,
  onDesbloquear,
  concluidoDias,
  onMudarConcluidoDias
}: KanbanColunaProps) {
  if (recolhida) {
    return (
      <div className={`flex flex-col bg-slate-50 border-r border-slate-200 shrink-0 h-full ${larguraColuna({ recolhida })}`}>
        <div className="p-2 border-b border-slate-200 flex flex-col items-center bg-white sticky top-0 z-10 shadow-sm gap-2">
          {onAlternarRecolher && (
            <button 
              type="button"
              onClick={onAlternarRecolher}
              aria-expanded={false}
              aria-label={`Expandir coluna ${coluna.titulo}`}
              className="p-1 rounded hover:bg-slate-100 min-h-[32px] min-w-[32px] flex items-center justify-center text-slate-500 hover:text-slate-700"
            >
              <ChevronsRight className="h-4 w-4" />
            </button>
          )}
          <span className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full font-medium mb-2">
            {coluna.totalCartoes}
          </span>
          <div className="[writing-mode:vertical-rl] rotate-180 text-[12px] font-semibold text-slate-700 uppercase tracking-wider flex-1 overflow-hidden mt-2">
            {coluna.titulo}
          </div>
        </div>
      </div>
    );
  }

  const isConcluidoOuExpedicao = coluna.tipo === 'CONCLUIDO' || coluna.tipo === 'EXPEDICAO';
  const showVerTodos = isConcluidoOuExpedicao && onMudarConcluidoDias;

  return (
    <div className={`flex flex-col bg-slate-50 border-r border-slate-200 shrink-0 h-full ${larguraColuna({ recolhida })}`}>
      <div className="p-3 border-b border-slate-200 flex justify-between items-center bg-white sticky top-0 z-10 shadow-sm">
        <h4 className="text-[12px] font-semibold text-slate-700 uppercase tracking-wider truncate flex-1">
          {coluna.titulo}
        </h4>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full font-medium">
            {coluna.totalCartoes}
          </span>
          {onAlternarRecolher && (
            <button 
              type="button"
              onClick={onAlternarRecolher}
              aria-expanded={true}
              aria-label={`Recolher coluna ${coluna.titulo}`}
              className="p-1 rounded hover:bg-slate-100 min-h-[32px] min-w-[32px] flex items-center justify-center text-slate-500 hover:text-slate-700"
            >
              <ChevronsLeft className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-2 flex flex-col gap-3 min-w-0">
        {showVerTodos && (
          <div className="flex flex-col items-center justify-center gap-1 mb-1">
            <span className="text-xs text-slate-500 text-center">
              {rotuloJanelaConcluido(concluidoDias === 'todos' ? null : concluidoDias || 7)}
            </span>
            <Button
              variant="link"
              size="sm"
              className="h-auto p-0 text-xs text-blue-600"
              onClick={() => onMudarConcluidoDias(concluidoDias === 'todos' ? 7 : 'todos')}
            >
              {concluidoDias === 'todos' ? 'Ver últimos 7 dias' : 'Ver todos'}
            </Button>
          </div>
        )}

        {renderRaias(coluna, raiasRecolhidas, onAlternarRaia, onSelecionarCartao, podeArrastar, avaliarRaia, quadro, onMover, onPrioridade, onResolverConflito, onDesbloquear)}
      </div>
    </div>
  );
}
